import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Check, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { translations } from "../../i18n";
import { trackContact, trackInitiateCheckout } from "../../lib/pixel";
import { buildRegisterUrl } from "../../lib/tracking-url";

type T = (typeof translations)["de"]["pricingTable"];

/**
 * Replaces the signup destination on every non-Enterprise plan. Used by
 * `/vorsprung-demo`, where no CTA may lead to the app's start page.
 */
type CtaOverride = {
  href: string;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

const ENTERPRISE_MAILTO = `mailto:neosearch@neobim.eu?cc=${encodeURIComponent(
  "marko@neobim.eu,zuraiz@neobim.eu",
)}&subject=${encodeURIComponent("Enterprise inquiry")}`;

// Render-time so forwarded utm_*/gclid/fbclid are always current.
const registerUrl = () => buildRegisterUrl("https://app.neosearch.neobim.ai/start");

/**
 * Comparison-table pricing.
 *
 * Single responsive layout — same DOM on desktop and mobile.
 *
 * - Desktop (md+): the whole table fits inside the viewport, no page scroll
 *   needed to see all plans + all rows. Plan columns flex evenly within
 *   max-w-[1100px].
 * - Mobile (<md): the feature-label column is fixed (sticky left) while
 *   plan columns scroll horizontally. Users compare plans side-by-side
 *   without the page itself growing tall. Pro column is anchored to be
 *   visible by default via a `scrollLeft` nudge on mount.
 */
function PricingTable({
  isVisible: _isVisible,
  t,
  theme = "dark",
  ctaOverride,
}: {
  isVisible?: (id: string) => boolean;
  t: T;
  theme?: "dark" | "light";
  ctaOverride?: CtaOverride;
}) {
  const [billing, setBilling] = useState<"monthly" | "yearly">("yearly");
  const isLight = theme === "light";

  const sectionBg = isLight ? "bg-slate-50" : "";
  const headingColor = isLight ? "text-slate-900" : "text-white";
  const subheadingColor = isLight ? "text-slate-600" : "text-slate-400";
  const toggleBorder = isLight
    ? "border-slate-300 bg-white"
    : "border-slate-800 bg-slate-900/60";
  const toggleInactive = isLight
    ? "text-slate-600 hover:text-slate-900"
    : "text-slate-300 hover:text-white";
  const toggleActive = isLight
    ? "bg-slate-900 text-white"
    : "bg-white text-slate-900";

  return (
    <section
      id="pricing"
      className={`py-14 sm:py-16 lg:py-24 px-5 sm:px-6 ${sectionBg}`}
    >
      <div className="max-w-[1100px] mx-auto">
        {/* Heading + toggle inline on desktop to save vertical space */}
        <div className="md:flex md:items-end md:justify-between md:gap-8 text-center md:text-left">
          <div>
            <h2
              className={`text-[22px] leading-[1.18] sm:text-[26px] md:text-[30px] md:leading-[1.1] font-bold tracking-tight ${headingColor}`}
            >
              {t.heading}
            </h2>
            <p
              className={`text-[13px] sm:text-[14px] leading-relaxed max-w-[520px] mx-auto md:mx-0 mt-1.5 ${subheadingColor}`}
            >
              {t.subheading}
            </p>
          </div>

          <div className="flex justify-center mt-4 md:mt-0 shrink-0">
            <div
              role="tablist"
              aria-label="Billing interval"
              className={`inline-flex items-center gap-1 p-1 rounded-full border ${toggleBorder}`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={billing === "monthly"}
                onClick={() => setBilling("monthly")}
                className={`px-3.5 py-1.5 text-[12px] font-semibold rounded-full transition-colors ${
                  billing === "monthly" ? toggleActive : toggleInactive
                }`}
              >
                {t.billingToggle.monthly}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={billing === "yearly"}
                onClick={() => setBilling("yearly")}
                className={`px-3.5 py-1.5 text-[12px] font-semibold rounded-full transition-colors flex items-center gap-1.5 ${
                  billing === "yearly" ? toggleActive : toggleInactive
                }`}
              >
                {t.billingToggle.yearly}
                <span
                  className={`text-[10px] font-bold leading-none rounded-full px-1.5 py-0.5 ${
                    billing === "yearly"
                      ? "bg-orange-500 text-white"
                      : "bg-orange-500/20 text-orange-600"
                  }`}
                >
                  {t.billingToggle.saveBadge}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Desktop: full comparison table */}
        <div className="hidden md:block">
          <ComparisonTable
            t={t}
            billing={billing}
            isLight={isLight}
            ctaOverride={ctaOverride}
          />
        </div>

        {/* Mobile: swipeable plan deck, Pro centered by default */}
        <div className="md:hidden">
          <MobileDeck
            t={t}
            billing={billing}
            isLight={isLight}
            ctaOverride={ctaOverride}
          />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function ComparisonTable({
  t,
  billing,
  isLight,
  ctaOverride,
}: {
  t: T;
  billing: "monthly" | "yearly";
  isLight: boolean;
  ctaOverride?: CtaOverride;
}) {
  // Section dividers are slightly stronger than per-row separators so the
  // eye reads the table as grouped chunks rather than one long ladder.
  const sectionLine = isLight ? "border-slate-200" : "border-slate-800";
  const rowLine = isLight ? "border-slate-100" : "border-slate-800/40";
  const groupLabelColor = isLight ? "text-slate-700" : "text-slate-300";
  const rowLabelColor = isLight ? "text-slate-800" : "text-slate-200";
  const valueColor = isLight ? "text-slate-900" : "text-slate-100";
  const dimColor = isLight ? "text-slate-300" : "text-slate-700";
  const colHighlightBg = isLight
    ? "bg-orange-100/70"
    : "bg-orange-500/[0.10]";

  // Slightly narrower feature column + equal plan columns keeps each plan
  // column tighter and stops Enterprise from feeling empty.
  const planCount = t.plans.length;
  const cols = `minmax(150px, 1.1fr) repeat(${planCount}, minmax(0, 1fr))`;

  // Pre-compute the Pro column index for the badge overlay (positioned by
  // percentage of the grid template width — no grid auto-placement risk).
  const proColumn = t.plans.findIndex((p) => p.highlight);
  const proGridCol = proColumn + 2; // 1 = feature label, 2.. = plan cols
  const TOTAL_FR = 1.1 + planCount;
  const COL_UNIT = 100 / TOTAL_FR;
  const proLeftPct = 1.1 * COL_UNIT + (proGridCol - 2) * COL_UNIT;
  const proWidthPct = COL_UNIT;

  return (
    <div className="mt-3 relative">
      {/* Floating "Beliebteste" pill — the only Pro emphasis sitting OUTSIDE
          the column. The peach background tint is the in-column emphasis. */}
      <div
        className="absolute z-20 pointer-events-none flex justify-center -top-2.5"
        style={{ left: `${proLeftPct}%`, width: `${proWidthPct}%` }}
      >
        <span className="bg-orange-500 text-white text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-0.5 rounded-full shadow-sm">
          {t.popularLabel}
        </span>
      </div>

      {/* Plan headers — rendered as one row of the data grid so the tint
          column extends naturally up into the header. */}
      <div
        className={`grid items-end pb-1 border-b ${sectionLine}`}
        style={{ gridTemplateColumns: cols }}
      >
        <div />
        {t.plans.map((p) => (
          <PlanHeaderCell
            key={p.key}
            plan={p}
            billing={billing}
            isLight={isLight}
            tint={p.highlight ? colHighlightBg : ""}
          />
        ))}
      </div>

      {/* Feature groups */}
      {t.groups.map((group, gi) => {
        const isLast = gi === t.groups.length - 1;
        return (
          <div
            key={gi}
            className={isLast ? "" : `border-b ${sectionLine}`}
          >
            {/* Section header row — orange bullet + ALLCAPS label */}
            <div className="grid" style={{ gridTemplateColumns: cols }}>
              <div
                className={`pt-2 pb-1 text-[11px] font-bold uppercase tracking-[0.14em] ${groupLabelColor}`}
              >
                <span className="inline-flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                  {group.label}
                </span>
              </div>
              {t.plans.map((p) => (
                <div
                  key={p.key}
                  className={p.highlight ? colHighlightBg : ""}
                />
              ))}
            </div>

            {group.rows.map((row, ri) => {
              const isLastRowInGroup = ri === group.rows.length - 1;
              return (
                <div
                  key={ri}
                  className="grid items-center"
                  style={{ gridTemplateColumns: cols }}
                >
                  <div
                    className={`py-2 pr-3 ${
                      isLastRowInGroup ? "" : `border-b ${rowLine}`
                    }`}
                  >
                    {row.tooltip ? (
                      <TooltipLabel
                        label={row.label}
                        tooltip={row.tooltip}
                        labelClass={`text-[13px] font-medium leading-snug ${rowLabelColor}`}
                        isLight={isLight}
                      />
                    ) : (
                      <div
                        className={`text-[13px] font-medium leading-snug ${rowLabelColor}`}
                        title={row.desc}
                      >
                        {row.label}
                      </div>
                    )}
                  </div>
                  {t.plans.map((p) => (
                    <div
                      key={p.key}
                      className={`text-center text-[13px] py-2 ${
                        isLastRowInGroup ? "" : `border-b ${rowLine}`
                      } ${
                        p.highlight ? colHighlightBg : ""
                      } ${valueColor}`}
                    >
                      <ValueCell
                        value={row.values[p.key]}
                        dimColor={dimColor}
                      />
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Slim bottom strip keeps the Pro tint contiguous into the
                section divider. */}
            <div className="grid" style={{ gridTemplateColumns: cols }}>
              <div className="pb-1" />
              {t.plans.map((p) => (
                <div
                  key={p.key}
                  className={`pb-1 ${p.highlight ? colHighlightBg : ""}`}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* CTAs row — each button sits flush in its plan column with a
          small top margin so they feel anchored. Pro column keeps its
          tint behind the orange CTA. */}
      <div
        className="grid items-start pt-2"
        style={{ gridTemplateColumns: cols }}
      >
        <div />
        {t.plans.map((p) => (
          <div
            key={p.key}
            className={`px-1.5 pb-2 ${p.highlight ? colHighlightBg : ""}`}
          >
            <PlanCta
              plan={p}
              billing={billing}
              isLight={isLight}
              ctaOverride={ctaOverride}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* MOBILE: swipeable plan deck, Pro centered by default                     */
/* ─────────────────────────────────────────────────────────────────────── */

function MobileDeck({
  t,
  billing,
  isLight,
  ctaOverride,
}: {
  t: T;
  billing: "monthly" | "yearly";
  isLight: boolean;
  ctaOverride?: CtaOverride;
}) {
  // Start on the first plan (Kostenlos) so the user reads top-down from the
  // entry tier. Hint of more-to-the-right is delivered via a peek animation
  // that fires when the deck first enters the viewport.
  const [idx, setIdx] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const suppressScrollRef = useRef(false);
  const didInitialScrollRef = useRef(false);

  // Mount-time: snap to the first card (instant, pre-paint).
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const child = el.children[0] as HTMLElement | undefined;
    if (!child) return;
    suppressScrollRef.current = true;
    const target =
      child.offsetLeft - (el.clientWidth - child.clientWidth) / 2;
    el.scrollLeft = target;
    didInitialScrollRef.current = true;
    const t = window.setTimeout(() => {
      suppressScrollRef.current = false;
    }, 200);
    return () => window.clearTimeout(t);
  }, []);

  // Auto-tour: continuously slide forward through every plan while the
  // deck is on screen, loop back to the start, repeat until the user
  // touches the deck.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const centerLeftFor = (i: number) => {
      const child = el.children[i] as HTMLElement | undefined;
      if (!child) return 0;
      return child.offsetLeft - (el.clientWidth - child.clientWidth) / 2;
    };

    let intervalId = 0;
    let step = 0;
    let userTookOver = false;
    const stepMs = 1100;
    const n = el.children.length;

    const start = () => {
      if (userTookOver || intervalId) return;
      suppressScrollRef.current = true;
      intervalId = window.setInterval(() => {
        if (userTookOver) {
          window.clearInterval(intervalId);
          intervalId = 0;
          return;
        }
        step = (step + 1) % n;
        el.scrollTo({ left: centerLeftFor(step), behavior: "smooth" });
        setIdx(step);
      }, stepMs);
    };

    const pause = () => {
      if (!intervalId) return;
      window.clearInterval(intervalId);
      intervalId = 0;
    };

    const onUserGesture = () => {
      userTookOver = true;
      pause();
      // Drop the suppress flag once the in-flight smooth-scroll settles
      // so the user's touch can move the deck freely.
      window.setTimeout(() => {
        suppressScrollRef.current = false;
      }, 200);
      el.removeEventListener("touchstart", onUserGesture);
      el.removeEventListener("pointerdown", onUserGesture);
    };

    el.addEventListener("touchstart", onUserGesture, { passive: true });
    el.addEventListener("pointerdown", onUserGesture, { passive: true });

    // Run only while the section is on screen. When it scrolls out we
    // pause; when it comes back into view we resume. Page scroll itself
    // never kills the tour — that was the bug that made the cards look
    // frozen on load.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (userTookOver) {
            observer.disconnect();
            return;
          }
          if (entry.isIntersecting) start();
          else pause();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      pause();
      el.removeEventListener("touchstart", onUserGesture);
      el.removeEventListener("pointerdown", onUserGesture);
    };
  }, []);

  // Programmatic scroll on dot / arrow taps; suppress the scroll listener
  // while we animate so it doesn't ping-pong idx back.
  useEffect(() => {
    if (!didInitialScrollRef.current) return;
    const el = trackRef.current;
    if (!el) return;
    const child = el.children[idx] as HTMLElement | undefined;
    if (!child) return;
    suppressScrollRef.current = true;
    const target =
      child.offsetLeft - (el.clientWidth - child.clientWidth) / 2;
    el.scrollTo({ left: target, behavior: "smooth" });
    const t = setTimeout(() => {
      suppressScrollRef.current = false;
    }, 700);
    return () => clearTimeout(t);
  }, [idx]);

  // Pick up the currently-centered card after a user swipe.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (suppressScrollRef.current) return;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const center = el.scrollLeft + el.clientWidth / 2;
        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i < el.children.length; i++) {
          const c = el.children[i] as HTMLElement;
          const cCenter = c.offsetLeft + c.clientWidth / 2;
          const d = Math.abs(cCenter - center);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        }
        setIdx((prev) => (prev === best ? prev : best));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const arrowBtn = isLight
    ? "bg-white border-slate-300 text-slate-700 hover:text-slate-900"
    : "bg-slate-900 border-slate-700 text-slate-300 hover:text-white";

  return (
    <div className="mt-8">
      <style>{`
        .adsv3-deck-track::-webkit-scrollbar { display: none; }
      `}</style>
      <div
        ref={trackRef}
        className="adsv3-deck-track flex overflow-x-auto snap-x snap-mandatory gap-4 -mx-5 px-5 pb-2"
        style={{ scrollbarWidth: "none" }}
      >
        {t.plans.map((p) => (
          <PlanDeckCard
            key={p.key}
            plan={p}
            billing={billing}
            isLight={isLight}
            t={t}
            ctaOverride={ctaOverride}
          />
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between gap-4 px-1">
        <button
          type="button"
          aria-label={t.mobile.prevLabel}
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          className={`w-9 h-9 rounded-full border flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed ${arrowBtn}`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2">
          {t.plans.map((p, i) => (
            <button
              key={p.key}
              type="button"
              aria-label={p.name}
              onClick={() => setIdx(i)}
              // ≥24px hit area around the tiny visual dot (a11y target-size)
              className="p-2.5 -m-1 flex items-center justify-center"
            >
              <span
                className={`h-1.5 rounded-full transition-all ${
                  i === idx
                    ? "w-6 bg-orange-500"
                    : isLight
                    ? "w-1.5 bg-slate-300"
                    : "w-1.5 bg-slate-700"
                }`}
              />
            </button>
          ))}
        </div>

        <button
          type="button"
          aria-label={t.mobile.nextLabel}
          onClick={() => setIdx((i) => Math.min(t.plans.length - 1, i + 1))}
          disabled={idx === t.plans.length - 1}
          className={`w-9 h-9 rounded-full border flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed ${arrowBtn}`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function PlanDeckCard({
  plan,
  billing,
  isLight,
  t,
  ctaOverride,
}: {
  plan: T["plans"][number];
  billing: "monthly" | "yearly";
  isLight: boolean;
  t: T;
  ctaOverride?: CtaOverride;
}) {
  const showYearly = billing === "yearly" && !!plan.priceYearly;
  const displayedPrice = showYearly ? plan.priceYearly! : plan.price;

  const cardBase = plan.highlight
    ? isLight
      ? "bg-orange-50 border-2 border-orange-500 shadow-lg shadow-orange-500/10"
      : "bg-orange-500/10 border-2 border-orange-500"
    : isLight
    ? "bg-white border border-slate-200 shadow-sm"
    : "bg-slate-900/60 border border-slate-800";

  const valueColor = isLight ? "text-slate-900" : "text-white";
  const descColor = isLight ? "text-slate-600" : "text-slate-400";
  const labelColor = isLight ? "text-slate-700" : "text-slate-300";

  return (
    <div
      className={`shrink-0 w-[85%] snap-center rounded-2xl p-6 flex flex-col ${cardBase}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <h3
          className={`text-lg font-bold ${
            plan.highlight ? "text-orange-600" : isLight ? "text-slate-900" : "text-white"
          }`}
        >
          {plan.name}
        </h3>
        {plan.highlight && (
          <span className="text-[10px] font-bold uppercase tracking-wide bg-orange-500 text-white px-2 py-0.5 rounded-full">
            {t.popularLabel}
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-1 mb-1">
        <span
          className={`text-[34px] font-black tracking-tight leading-none ${valueColor}`}
        >
          {displayedPrice}
        </span>
        {plan.period && (
          <span className={`text-sm ${descColor}`}>{plan.period}</span>
        )}
      </div>
      <div className="min-h-[18px] mb-5">
        {showYearly && plan.priceYearlyNote && (
          <p className={`text-[11px] ${descColor}`}>{plan.priceYearlyNote}</p>
        )}
      </div>

      <ul className="space-y-2 flex-1 mb-5">
        {t.groups.flatMap((group) =>
          group.rows
            .filter((row) => {
              const v = row.values[plan.key];
              return v === true || (typeof v === "string" && v.length > 0);
            })
            .map((row) => {
              const v = row.values[plan.key];
              return (
                <li
                  key={`${plan.key}-${row.label}`}
                  className="flex items-start gap-2"
                >
                  <Check className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className={`text-[13px] leading-snug ${labelColor} flex-1`}>
                    {typeof v === "string" && (
                      <strong className={`${valueColor} mr-1`}>{v}</strong>
                    )}
                    {row.tooltip ? (
                      <TooltipLabel
                        label={row.label}
                        tooltip={row.tooltip}
                        labelClass={`text-[13px] leading-snug ${labelColor}`}
                        isLight={isLight}
                      />
                    ) : (
                      row.label
                    )}
                  </span>
                </li>
              );
            }),
        )}
      </ul>

      <PlanCta
        plan={plan}
        billing={billing}
        isLight={isLight}
        ctaOverride={ctaOverride}
      />
    </div>
  );
}

function PlanHeaderCell({
  plan,
  billing,
  isLight,
  tint,
}: {
  plan: T["plans"][number];
  billing: "monthly" | "yearly";
  isLight: boolean;
  tint: string;
}) {
  const showYearly = billing === "yearly" && !!plan.priceYearly;
  const displayedPrice = showYearly ? plan.priceYearly! : plan.price;
  const nameColor = plan.highlight
    ? "text-orange-600"
    : isLight
    ? "text-slate-600"
    : "text-slate-300";
  const priceColor = isLight ? "text-slate-900" : "text-white";
  const periodColor = isLight ? "text-slate-500" : "text-slate-400";

  return (
    <div className={`text-center px-1.5 pt-3 pb-2 ${tint}`}>
      <div
        className={`text-[11px] font-bold uppercase tracking-[0.14em] ${nameColor}`}
      >
        {plan.name}
      </div>
      <div
        className={`mt-2 text-[24px] font-black tracking-tight leading-none ${priceColor}`}
      >
        {displayedPrice}
      </div>
      <div className={`text-[10px] mt-0.5 ${periodColor} min-h-[14px]`}>
        {plan.period || " "}
      </div>
    </div>
  );
}

function ValueCell({
  value,
  dimColor,
}: {
  value: string | boolean | undefined;
  dimColor: string;
}) {
  if (value === true) {
    return (
      <span className="inline-flex">
        <Check className="w-4 h-4 text-orange-500" />
      </span>
    );
  }
  if (value === false || value === undefined) {
    return <span className={dimColor}>—</span>;
  }
  return <span>{value}</span>;
}

function TooltipLabel({
  label,
  tooltip,
  labelClass,
  isLight,
}: {
  label: string;
  tooltip: string;
  labelClass: string;
  isLight: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointer);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);
  const bubbleBg = isLight
    ? "bg-slate-900 text-white"
    : "bg-white text-slate-900";
  const arrowColor = isLight ? "bg-slate-900" : "bg-white";
  return (
    <span
      ref={wrapRef}
      className="relative inline-flex items-start gap-1 cursor-help touch-manipulation"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={(e) => {
        e.stopPropagation();
        setOpen((v) => !v);
      }}
      tabIndex={0}
      role="button"
      aria-expanded={open}
    >
      <span className={labelClass}>{label}</span>
      <Info
        aria-hidden="true"
        className="w-3.5 h-3.5 mt-[2px] text-orange-500 shrink-0"
      />
      {open && (
        <span
          role="tooltip"
          className={`absolute left-0 top-full mt-1.5 z-30 w-64 max-w-[80vw] rounded-md ${bubbleBg} px-3 py-2 text-[12px] leading-relaxed font-normal normal-case tracking-normal shadow-lg`}
        >
          <span
            aria-hidden="true"
            className={`absolute -top-1 left-3 h-2 w-2 rotate-45 ${arrowColor}`}
          />
          {tooltip}
        </span>
      )}
    </span>
  );
}

function PlanCta({
  plan,
  billing,
  isLight,
  ctaOverride,
}: {
  plan: T["plans"][number];
  billing: "monthly" | "yearly";
  isLight: boolean;
  ctaOverride?: CtaOverride;
}) {
  // Enterprise always stays a mailto; the override only replaces signup.
  const overridden = !plan.contact && !!ctaOverride;
  const href = plan.contact
    ? ENTERPRISE_MAILTO
    : ctaOverride?.href ?? registerUrl();
  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (plan.contact) return trackContact({ source: "pricing_table_enterprise" });
    if (overridden) return ctaOverride!.onClick(e);
    trackInitiateCheckout({
      source: "pricing_table",
      plan: plan.name,
      billing,
    });
  };

  const baseCta = plan.highlight
    ? "bg-orange-500 hover:bg-orange-600 text-white"
    : isLight
    ? "bg-slate-900 hover:bg-slate-800 text-white"
    : "bg-slate-800 hover:bg-slate-700 text-white";

  return (
    <a
      href={href}
      onClick={onClick}
      className={`block w-full text-center px-2 py-2 text-[12px] font-semibold rounded-md transition-colors hover:-translate-y-px active:scale-95 ${baseCta}`}
    >
      {plan.cta}
    </a>
  );
}

export default PricingTable;
